import { useState, useContext, useEffect, useCallback } from "react";
import { Table } from "antd";
import { DataTableContext } from "../context/DataTableContext";
import { Columns } from "../components/PageData";
import { useHistory } from "react-router-dom";
import { UserTypes } from "../interfaces/User";
import ActionMenu from "../components/ActionMenu";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 0;

export interface Props {
  columns: Columns[];
  useGetQuery: any;
  useRemoveMutation: any;
  modify: boolean;
  hasDelete?: boolean;
  type: string;
  hostId?: string;
}

function useDataTable({
  columns,
  useGetQuery,
  modify,
  hasDelete,
  type,
  hostId,
}: Props) {
  const history = useHistory();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [filter, setFilter] = useState({});
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState<null | "asc" | "desc">(null);
  const { search, responseObjName } = useContext(DataTableContext);
  const getTableColumns = useCallback(
    (currentP: number, pageS: number): Columns[] => [
      {
        title: "No",
        dataIndex: "key",
        key: "key",
        width: 100,
        render: (text: any, record: any, index: any) => {
          return currentP * pageS + index + 1;
        },
      },
      ...columns,
      ...(modify || hasDelete
        ? [
            {
              title: "Action",
              key: "action",
              render: (text: any, record: any, index: any) => (
                <ActionMenu
                  record={record}
                  hasEdit={modify}
                  hasDelete={hasDelete}
                />
              ),
              width: 100,
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns]
  );
  const [columnsState, setColumnsState] = useState(() =>
    getTableColumns(currentPage, pageSize)
  );
  useEffect(() => {
    setColumnsState(getTableColumns(currentPage, pageSize));
  }, [currentPage, pageSize, getTableColumns]);

  const { data, loading, refetch } = useGetQuery({
    variables: {
      page: currentPage + 1,
      limit: pageSize,
      sortField,
      sortOrder,
      search,
      filter: JSON.stringify(filter),
      type,
      hostId,
    },
    fetchPolicy: "cache-and-network",
  });

  // const [actionColumnView] = useActionMenu({
  //   useRemoveMutation,
  //   refetch,
  //   userType: type,
  // });
  const hasSelected = selectedRowKeys.length > 0;

  const rowSelection = {
    selectedRowKeys,
    onChange: (selected: any) => {
      setSelectedRowKeys(selected);
    },
  };

  const resetPagination = () => {
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter && sorter.field) {
      const resetColumns = columnsState.map((col) => {
        if (col.sortOrder) {
          col.sortOrder = undefined;
        }
        if (col.key === sorter.field) {
          col.sortOrder = sorter.order;
        }
        return col;
      });
      const newState = resetColumns;
      setColumnsState(newState);
    }
    setFilter(filters);
    for (const property in filters) {
      const index = columnsState.findIndex((col) => col.key === property);
      columnsState[index] = {
        ...columnsState[index],
        filteredValue: filters[property],
      };
      const newState = columnsState;
      setColumnsState(newState);
    }
    setCurrentPage(pagination.current - 1);
    setPageSize(pagination.pageSize);
    setSortField(sorter.field);
    setSortOrder(sortOrderTransform(sorter.order));
  };

  const sortOrderTransform = (order: string) => {
    switch (order) {
      case "ascend":
        return "asc";
      case "descend":
        return "desc";
      default:
        return null;
    }
  };

  const DataTable = () => (
    <Table
      sortDirections={["ascend", "descend"]}
      loading={loading}
      rowKey={(record) => record.id}
      rowSelection={rowSelection}
      columns={columnsState}
      dataSource={data && responseObjName && data[responseObjName].result}
      className="time-table-row-select"
      onRow={(record) => {
        return {
          onClick: (event) => {
            setSelectedRow(record);
            const element = event.target as HTMLElement;
            if (
              record.type === UserTypes.instructor &&
              element.tagName !== "svg" &&
              element.tagName !== "LI" &&
              element.tagName !== "path"
            ) {
              history.push(`/instructors/${record.id}`);
            }
            if (
              record.type === UserTypes.student &&
              element.tagName !== "svg" &&
              element.tagName !== "LI" &&
              element.tagName !== "path"
            ) {
              history.push(`/user/${record.id}`);
            }
          },
        };
      }}
      onChange={handleTableChange}
      pagination={{
        showSizeChanger: true,
        pageSize: pageSize,
        current: currentPage + 1,
        total: data && responseObjName && data[responseObjName].total,
        showTotal: (total, range) => {
          return `${range[0]}-${range[1]} of ${total} items`;
        },
      }}
    />
  );

  return {
    DataTable,
    hasSelected,
    selectedRow,
    selectedRowKeys,
    currentPage,
    pageSize,
    resetPagination,
    refetch,
  };
}

export default useDataTable;
